import CommandPollingManager from '../polling/CommandPollingManager';
import IPauseState from '../session/models/IPauseState';
import IPauseManagerNotificationObserver from './IPauseManagerNotificationObserver';
import IPauseStateUpdateHandler from './IPauseStateUpdateHandler';

export class PauseManager {
  private readonly simulate: boolean;

  private readonly pauseManagerNotificationObservers: Array<IPauseManagerNotificationObserver> =
    new Array<IPauseManagerNotificationObserver>();
  private readonly pollingManager: CommandPollingManager<unknown> | undefined;
  private readonly stateUpdateHandler: IPauseStateUpdateHandler | undefined | null;
  private busy = false;
  private _currentPauseState: PauseState = PauseState.UNPAUSE;

  constructor(
    simulate: boolean,
    pollingManager: CommandPollingManager<unknown> | undefined,
    stateUpdateHandler: IPauseStateUpdateHandler | undefined | null
  ) {
    this.simulate = simulate;

    if (!simulate && (pollingManager === undefined || stateUpdateHandler === undefined)) {
      throw new Error('pollingManager and stateUpdateHandler cannot be undefined');
    }

    this.pollingManager = pollingManager;
    this.stateUpdateHandler = stateUpdateHandler;
  }

  addPauseManagerNotificationObserver(observer: IPauseManagerNotificationObserver | undefined | null): void {
    if (observer) {
      this.removePauseManagerNotificationObserver(observer);
      this.pauseManagerNotificationObservers.push(observer);
    }
  }

  removePauseManagerNotificationObserver(observer: IPauseManagerNotificationObserver | undefined): void {
    if (observer) {
      const index = this.pauseManagerNotificationObservers.indexOf(observer, 0);
      if (index > -1) {
        this.pauseManagerNotificationObservers.splice(index, 1);
      }
    }
  }

  get currentPauseState(): PauseState {
    return this._currentPauseState;
  }

  private notifyObservers(type: PauseType, state: PauseState): void {
    this._currentPauseState = state;
    this.pauseManagerNotificationObservers.forEach((observer) => {
      observer.pauseStateNotification(type, state);
    });
  }

  private notifyObserversOfError(error: Error): void {
    this.pauseManagerNotificationObservers.forEach((observer) => {
      observer.pauseError(error);
    });
  }

  pauseTest(type: PauseType): void {
    if (!this.busy) {
      this.busy = true;

      if (this.simulate) {
        this.notifyObservers(type, PauseState.PAUSE);
        this.busy = false;
      } else {
        this.pollingManager!.stop();
        this.notifyObservers(type, PauseState.PAUSE);
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        this.stateUpdateHandler!.updatePauseState(this.getPauseStateValue(type))
          .then((data) => {
            that.handleUpdatePauseStateReturn(data);
          })
          .catch((error) => {
            that.notifyObserversOfError(error);
          })
          .finally(() => {
            that.busy = false;
            that.pollingManager!.start();
          });
      }
    }
  }

  unpauseTest(): void {
    if (!this.busy) {
      this.busy = true;
      if (this.simulate) {
        this.handleUnpause();
        this.busy = false;
      } else {
        this.pollingManager!.stop();
        this.handleUnpause();
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        this.stateUpdateHandler
          ?.updatePauseState(PauseStateValue.IN_PROGRESS)
          .then((data) => {
            that.handleUpdatePauseStateReturn(data);
          })
          .catch((error) => {
            that.notifyObserversOfError(error);
          })
          .finally(() => {
            that.busy = false;
            that.pollingManager!.start();
          });
      }
    }
  }

  private handleUnpause(): void {
    this.notifyObservers(PauseType.SECTION_PAUSE, PauseState.UNPAUSE);
    this.notifyObservers(PauseType.PROCTOR_PAUSE, PauseState.UNPAUSE);
    this.notifyObservers(PauseType.TESTER_PAUSE, PauseState.UNPAUSE);
  }

  private handleUpdatePauseStateReturn(data: IPauseState | void): void {
    if (data && data.status === 'ok') {
      const pauseStateValue: PauseStateValue | undefined = PauseManager.stringToPauseStateValue(data.state);
      if (pauseStateValue) {
        this.handlePauseState(pauseStateValue);
      }
    }
  }

  handlePauseState(state: PauseStateValue): void {
    switch (state) {
      case PauseStateValue.PAUSE: {
        this.notifyObservers(PauseType.PROCTOR_PAUSE, PauseState.PAUSE);
        break;
      }
      case PauseStateValue.SECTION_PAUSE: {
        this.notifyObservers(PauseType.SECTION_PAUSE, PauseState.PAUSE);
        break;
      }
      case PauseStateValue.TESTER_PAUSE: {
        this.notifyObservers(PauseType.TESTER_PAUSE, PauseState.PAUSE);
        break;
      }
      case PauseStateValue.RESEAT: {
        this.notifyObservers(PauseType.RESEAT, PauseState.RESEAT);
        break;
      }
      case PauseStateValue.SUBMITTED: {
        this.notifyObservers(PauseType.SUBMITTED, PauseState.SUBMITTED);
        break;
      }
      case PauseStateValue.IN_PROGRESS: {
        this.notifyObservers(PauseType.PROCTOR_PAUSE, PauseState.UNPAUSE);
        this.notifyObservers(PauseType.SECTION_PAUSE, PauseState.UNPAUSE);
        this.notifyObservers(PauseType.TESTER_PAUSE, PauseState.UNPAUSE);
        break;
      }
      default: {
        //Do nothing
      }
    }
  }

  static stringToPauseStateValue(value: string): PauseStateValue | undefined {
    switch (value) {
      case 'PAUSED': {
        return PauseStateValue.PAUSE;
      }
      case 'PAUSED_SECTION': {
        return PauseStateValue.SECTION_PAUSE;
      }
      case 'PAUSED_TESTER': {
        return PauseStateValue.TESTER_PAUSE;
      }
      case 'RESEAT': {
        return PauseStateValue.RESEAT;
      }
      case 'SUBMITTED': {
        return PauseStateValue.SUBMITTED;
      }
      case 'IN_PROGRESS': {
        return PauseStateValue.IN_PROGRESS;
      }
      default: {
        return undefined;
      }
    }
  }

  private getPauseStateValue(type: PauseType): PauseStateValue {
    switch (type) {
      case PauseType.PROCTOR_PAUSE: {
        return PauseStateValue.PAUSE;
      }
      case PauseType.SECTION_PAUSE: {
        return PauseStateValue.SECTION_PAUSE;
      }
      case PauseType.TESTER_PAUSE: {
        return PauseStateValue.TESTER_PAUSE;
      }
      case PauseType.RESEAT: {
        return PauseStateValue.RESEAT;
      }
      case PauseType.SUBMITTED: {
        return PauseStateValue.SUBMITTED;
      }
      default: {
        return PauseStateValue.IN_PROGRESS;
      }
    }
  }
}

export enum PauseStateValue {
  PAUSE = 'PAUSED',
  TESTER_PAUSE = 'PAUSED_TESTER',
  SECTION_PAUSE = 'PAUSED_SECTION',
  IN_PROGRESS = 'IN_PROGRESS',
  RESEAT = 'RESEAT',
  SUBMITTED = 'SUBMITTED',
}

export enum PauseType {
  TESTER_PAUSE,
  PROCTOR_PAUSE,
  SECTION_PAUSE,
  RESEAT,
  SUBMITTED,
}

export enum PauseState {
  PAUSE,
  UNPAUSE,
  RESEAT,
  SUBMITTED,
}
